import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { ConnectionState } from "../sdk/types/transport";
import {
  RawBucket,
  BucketsUpdateMessage,
  MultiFeedBucketsData,
  MultiFeedBucketData,
} from "../sdk/types/api";
import {
  BucketCollectionsConfig,
  Bucket,
  transformRawBucketToBucket,
  convertMultiFeedToRawBucket,
  isRawBucket,
} from "../sdk/types/data";

// Re-export for backward compatibility
export type { BucketCollectionsConfig };

/**
 * Hook for managing bucket collections data
 * Fetches buckets from API and calculates collection metrics
 */
export function useBucketCollections(config: BucketCollectionsConfig): {
  collections: Bucket[];
  loading: boolean;
  error: string;
  refreshBucketBalance: () => void;
  refreshSpotWalletBalance: () => Promise<void>;
  spotWalletBalance: number;
  isConnected: boolean;
} {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [collections, setCollections] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [spotWalletBalance, setSpotWalletBalance] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);
  const isSubscribedRef = useRef<boolean>(false);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Transform bucket data from WebSocket to Bucket format
   * Handles both RawBucket and MultiFeedBucketData types from WebSocket
   * HTTP responses always use transformRawBucketToBucket directly
   */
  const transformBucket = useCallback(
    (bucket: RawBucket | MultiFeedBucketData): Bucket => {
      // Convert MultiFeedBucketData to RawBucket if needed
      const rawBucket: RawBucket = isRawBucket(bucket)
        ? bucket
        : convertMultiFeedToRawBucket(bucket);

      // Transform raw API data to Bucket type (handles satoshi conversion)
      return transformRawBucketToBucket(rawBucket);
    },
    [],
  );

  /**
   * Fetch spot wallet balance for USDC from API
   */
  const fetchSpotWalletBalance = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      setSpotWalletBalance(0);
      return;
    }

    try {
      // Get USDC balance using getBalanceDecimal - converts satoshi strings to decimal numbers
      const balance = await infoClientRef.current.getBalanceDecimal(
        currentConfig.walletAddress,
        "USDC", // Specify USDC asset
      );
      // getBalanceDecimal converts available_balance to available as a number
      // Use available field (already converted to decimal), with fallback for edge cases
      const available = typeof balance.available === 'number' 
        ? balance.available
        : (balance.availableBalance ? toNumberFromSatoshi(balance.availableBalance) : 0);
      setSpotWalletBalance(available);
    } catch (err) {
      console.warn("Failed to fetch spot wallet balance (USDC):", err);
      setSpotWalletBalance(0);
    }
  }, []);

  /**
   * Fetch bucket collections from API
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchCollections = useCallback(async () => {
    if (!infoClientRef.current) return;
    const currentConfig = configRef.current;
    // Validate walletAddress (required field, but check for runtime safety)
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and collections when address is not available
      setError("Wallet address is required");
      setCollections([]);
      setLoading(false);
      isInitialLoadRef.current = false;
      return;
    }
    setError("");
    // Only show loading state on initial load, not on refreshes
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    try {
      console.log(
        "[useBucketCollections] [fetchCollections] Starting fetch...",
        {
          walletAddress: currentConfig.walletAddress,
          useWebSocket,
          isSubscribed: isSubscribedRef.current,
        },
      );

      // Fetch spot wallet balance in parallel
      fetchSpotWalletBalance();

      // Fetch buckets from API
      console.log(
        "[useBucketCollections] [fetchCollections] Calling getBuckets API...",
      );
      const response = await infoClientRef.current.getBuckets(
        currentConfig.walletAddress,
      );
      console.log(
        "[useBucketCollections] [fetchCollections] Raw API response:",
        {
          response,
          responseType: typeof response,
          isArray: Array.isArray(response),
          responseLength: Array.isArray(response) ? response.length : "N/A",
        },
      );

      const bucketsData = Array.isArray(response) ? response : [];
      console.log(
        "[useBucketCollections] [fetchCollections] Buckets data after normalization:",
        {
          bucketsData,
          bucketsDataLength: bucketsData.length,
          bucketsDataTypes: bucketsData.map((b, i) => ({
            index: i,
            type: typeof b,
            hasBucketId: !!(b?.bucketId),
            bucketId: (b as RawBucket).bucketId ?? "",
          })),
        },
      );

      // Transform buckets to collections
      console.log(
        `[useBucketCollections] [fetchCollections] About to transform ${bucketsData.length} buckets:`,
        bucketsData,
      );

      // HTTP API always returns RawBucket[], so use transformRawBucketToBucket directly
      const transformedCollections: Bucket[] = bucketsData.map(
        transformRawBucketToBucket,
      );

      setCollections(transformedCollections);

      // Only mark initial load as complete if WebSocket is not enabled
      // If WebSocket is enabled, it will handle setting loading to false when it receives data
      if (!useWebSocket || !isSubscribedRef.current) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load bucket collections";
      setError(errorMessage);
      console.error(
        "[useBucketCollections] [fetchCollections] Failed to fetch bucket collections:",
        {
          error: err,
          errorMessage,
          walletAddress: currentConfig.walletAddress,
          stack: err instanceof Error ? err.stack : undefined,
        },
      );

      // If WebSocket is not enabled or failed to connect, mark initial load as complete
      // Otherwise, wait for WebSocket to provide data or fail
      if (!useWebSocket || !isSubscribedRef.current) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    }
  }, [fetchSpotWalletBalance, useWebSocket]); // transformRawBucketToBucket is a pure function, no dependencies needed

  /**
   * Handle bucket updates from WebSocket (multi-feed buckets sub-channel)
   * Backend sends MultiFeedBucketsData with buckets array for both initial snapshots and updates
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleBucketsUpdate = useCallback(
    (data: MultiFeedBucketsData | BucketsUpdateMessage) => {
      console.log(
        "[useBucketCollections] [handleBucketsUpdate] WebSocket data received:",
        {
          data,
          hasBuckets: !!data.buckets,
          bucketsIsArray: Array.isArray(data.buckets),
          bucketsLength: Array.isArray(data.buckets) ? data.buckets.length : 0,
        },
      );
      // WebSocket format: { buckets: [...], total_buckets: number, address: string, timestamp: number }
      if (data.buckets && Array.isArray(data.buckets)) {
        console.log(
          "[useBucketCollections] [handleBucketsUpdate] Transforming WebSocket buckets:",
          data.buckets,
        );
        const transformedCollections: Bucket[] =
          data.buckets.map(transformBucket);
        console.log(
          "[useBucketCollections] [handleBucketsUpdate] Transformed collections:",
          transformedCollections,
        );

        // Filter out any collections with empty bucketId or status is "dormant" (same as HTTP fetch)
        const validCollections = transformedCollections.filter(
          (c) =>
            c.bucketId && c.bucketId.trim() !== "" && c.status !== "dormant",
        );

        if (validCollections.length !== transformedCollections.length) {
          const filteredCount =
            transformedCollections.length - validCollections.length;
          const filteredByBucketId = transformedCollections.filter(
            (c) => !c.bucketId || c.bucketId.trim() === "",
          );
          const filteredByStatus = transformedCollections.filter(
            (c) =>
              c.bucketId && c.bucketId.trim() !== "" && c.status === "dormant",
          );
          console.warn(
            `[useBucketCollections] [handleBucketsUpdate] WebSocket: Filtered out ${filteredCount} collections (${filteredByBucketId.length} with empty bucketId, ${filteredByStatus.length} with status "dormant")`,
            {
              filteredByBucketId,
              filteredByStatus,
            },
          );
        }

        console.log(
          "[useBucketCollections] [handleBucketsUpdate] Setting collections from WebSocket:",
          {
            validCollectionsCount: validCollections.length,
            validCollections,
          },
        );

        // Compare with current collections to avoid unnecessary state updates
        setCollections((prev) => {
          console.log(
            "[useBucketCollections] [handleBucketsUpdate] Previous collections:",
            {
              prevCount: prev.length,
              prev,
            },
          );
          if (prev.length !== validCollections.length) {
            return validCollections;
          }
          // Deep comparison - only update if collections actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(validCollections);
          return prevStr === newStr ? prev : validCollections;
        });
        // Mark initial load as complete when we receive websocket data
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          setLoading(false);
        }
      }
    },
    [transformBucket],
  );

  // Initialize client and fetch collections
  useEffect(() => {
    // Validate required config fields (runtime safety check)
    if (!config.walletAddress || config.walletAddress.trim() === "") {
      setLoading(false);
      setError("Wallet address is required");
      setCollections([]);
      // Clean up any existing subscriptions
      if (subscriptionClientRef.current && isSubscribedRef.current) {
        try {
          subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
            "buckets",
            handleBucketsUpdate,
          );
        } catch (error) {
          console.warn(
            "[useBucketCollections] Error unsubscribing during cleanup:",
            error,
          );
        }
        isSubscribedRef.current = false;
      }
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = 0;
      }
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;
    isInitialLoadRef.current = true; // Reset initial load flag when config changes
    isSubscribedRef.current = false; // Reset subscription flag

    // Initial fetch from HTTP (always do this for fast initial load)
    console.log(
      "[useBucketCollections] [useEffect] Initializing, calling fetchCollections...",
    );
    fetchCollections();

    const subscriptionClient = config.subscriptionClient;

    if (useWebSocket) {
      // WebSocket mode: primary source
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        // Check if ready for subscriptions (connected and authenticated if required)
        if (!subscriptionClient.isReadyForSubscriptions()) {
          if (
            subscriptionClient.isConnected() &&
            !subscriptionClient.isReadyForSubscriptions()
          ) {
            // Connected but not authenticated - wait a bit and retry
            const authState = subscriptionClient.getAuthState();
            if (authState === "pending") {
              console.debug(
                "[useBucketCollections] Waiting for authentication to complete...",
              );
              // Will retry on next state change
            } else if (authState === "failed") {
              console.error(
                "[useBucketCollections] Authentication failed - cannot subscribe to buckets",
              );
              setError("Authentication failed. Please re-authenticate.");
            }
          }
          return;
        }

        // Check if we need to re-subscribe (marketId or symbol changed, or not yet subscribed)
        const marketId = String(config.marketId ?? "0"); // Default to "0" for user-global, ensure string
        const symbol = config.symbol ?? "USDC/USDC"; // Default symbol for user-global
        const needsResubscribe = !isSubscribedRef.current;

        if (needsResubscribe) {
          // Unsubscribe from previous subscription if it exists
          if (isSubscribedRef.current && subscriptionClientRef.current) {
            try {
              subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
                "buckets",
                handleBucketsUpdate,
              );
            } catch (error) {
              console.warn(
                "[useBucketCollections] Error unsubscribing from previous buckets subscription:",
                error,
              );
            }
          }

          try {
            // Use multi-feed sub-channel for buckets (same subscription as other user data)
            subscriptionClient.subscribeMultiFeedSubChannel(
              "buckets",
              marketId,
              handleBucketsUpdate,
              { symbol },
            );
            isSubscribedRef.current = true;
            setLoading(false);
            console.debug("[useBucketCollections] Subscribed to buckets:", {
              marketId,
              symbol,
            });
          } catch (error) {
            console.error(
              "[useBucketCollections] Failed to subscribe to buckets:",
              error,
            );
            setError(
              error instanceof Error
                ? error.message
                : "Failed to subscribe to buckets",
            );
            isSubscribedRef.current = false;
            setLoading(false);
          }
        }
      };

      // Set initial connection state
      setIsConnected(subscriptionClient.isConnected());
      setLoading(false);

      // Try to subscribe immediately if client is ready
      if (subscriptionClient.isReadyForSubscriptions()) {
        subscribe();
      }

      // Monitor connection state changes and re-subscribe on reconnection
      // Use the public onStateChange method from SubscriptionClient
      const unsubscribeStateChange = subscriptionClient.onStateChange(
        (state: ConnectionState) => {
          console.debug(
            "[useBucketCollections] WebSocket state changed to:",
            state,
          );
          setIsConnected(state === ConnectionState.Connected);
          if (state === ConnectionState.Connected) {
            // Always try to subscribe on connection (subscribe function handles re-subscription logic)
            subscribe();
            // Stop polling when WS is connected
            if (refreshIntervalRef.current !== null) {
              clearInterval(refreshIntervalRef.current);
              refreshIntervalRef.current = 0;
            }
          } else {
            // Start polling when WS disconnects
            if (
              state === ConnectionState.Disconnected &&
              config.refreshInterval &&
              config.refreshInterval > 0 &&
              refreshIntervalRef.current === null
            ) {
              refreshIntervalRef.current = window.setInterval(() => {
                fetchCollections();
              }, config.refreshInterval);
            }
          }
        },
      );

      return () => {
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }
        if (subscriptionClientRef.current && isSubscribedRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "buckets",
              handleBucketsUpdate,
            );
          } catch (error) {
            console.warn(
              "[useBucketCollections] Error unsubscribing during cleanup:",
              error,
            );
          }
        }
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
        // Reset subscription state
        isSubscribedRef.current = false;
      };
    } else {
      // Polling mode: set up auto-refresh if interval is specified
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchCollections();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.subscriptionClient,
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    config.marketId,
    config.symbol,
    useWebSocket,
    handleBucketsUpdate,
    // Note: fetchCollections is intentionally NOT in dependencies
    // fetchCollections uses configRef to access latest config without causing dependency loops
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  // Log what we're returning to the component
  useEffect(() => {
    console.log(
      "[useBucketCollections] [useEffect] Hook state changed, returning:",
      {
        collectionsCount: collections.length,
        collections,
        loading,
        error,
        spotWalletBalance,
      },
    );
  }, [collections, loading, error, spotWalletBalance]);

  return {
    collections,
    loading,
    error,
    refreshBucketBalance: fetchCollections,
    refreshSpotWalletBalance: fetchSpotWalletBalance,
    spotWalletBalance,
    isConnected,
  };
}
